/**
 * Turning API payloads into form default values.
 *
 * These live outside the components that use them for two reasons: they are
 * pure and worth proving in node, and a `.tsx` file that exports a non-
 * component trips `react-refresh/only-export-components`, which admin counts as
 * a lint warning against a fixed ceiling.
 */

import { defaultFieldValues, describeBody, flattenFactValues, flattenFields } from './editor-model';
import type { Block, BlogLocaleCopy, FactDefinition } from './types';
import type { BlogClusterFormInput, BlogLocaleFormInput } from '@/lib/schemas/blog-schemas';

/**
 * One locale copy, as its form sees it.
 *
 * The cluster's fact definitions are a parameter rather than something read off
 * the copy (AM-18): the copy carries only the renderings it happens to have,
 * and the form must show a row for every fact the post *defines* — including
 * the ones this language has not written yet, which are exactly the rows that
 * matter.
 */
export function buildLocaleFormDefaults(
  copy: BlogLocaleCopy,
  facts: readonly FactDefinition[],
): BlogLocaleFormInput {
  const flat = flattenFields(describeBody(copy.body));
  const factFields = flattenFactValues(facts, copy.factValues);
  return {
    title: copy.title,
    description: copy.description,
    excerpt: copy.excerpt,
    pathSlug: copy.pathSlug ?? '',
    keywords: copy.keywords.join('\n'),
    glossary: copy.glossary.map((entry) => ({ ...entry })),
    factValues: Object.fromEntries(factFields.map((field) => [field.key, field.value])),
    fields: defaultFieldValues(flat),
  };
}

/**
 * Fact definitions and hands-on minutes, as the cluster panel's form sees them.
 *
 * `minutes` carries the block index so a save can address the step it belongs
 * to without the server re-deriving positions from titles, which are
 * translated. The facts carry no value: what a fact reads as is language, and
 * this form is the one place that is never allowed to say it.
 */
export function buildClusterFormDefaults(
  facts: readonly FactDefinition[],
  body: readonly Block[],
): BlogClusterFormInput {
  return {
    facts: facts.map((fact) => ({ ...fact })),
    minutes: body.flatMap((block, index) =>
      block.kind === 'step' ? [{ index, title: block.title, minutes: block.minutes }] : [],
    ),
  };
}
